"use client";

import { c as _c } from "react/compiler-runtime";
import { buildInboundPostHogProperties, getInboundAttribution } from "@/lib/inbound-attribution";
import type { ScheduleCallPreset } from "@/lib/zoom-scheduler";
import { Badge } from "@packages/ui/components/badge";
import { Button } from "@packages/ui/components/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@packages/ui/components/dialog";
import { Link } from "@packages/ui/components/link";
import { Spinner } from "@packages/ui/components/spinner";
import { cn } from "@packages/ui/lib/utils";
import { ArrowRight, CalendarClock, Clock3, MessagesSquare, X } from "lucide-react";
import type * as React from "react";
import { useEffect, useState } from "react";
import posthog from "posthog-js";
type LoadState = "error" | "idle" | "loading" | "ready";
interface ScheduleCallButtonProps extends Pick<React.ComponentProps<typeof Button>, "className" | "size" | "variant"> {
  url: string;
  children: React.ReactNode;
  preset: ScheduleCallPreset;
  showArrow?: boolean;
  theme?: "dark" | "light";
}
interface ScheduleCallPresetContent {
  badge: string;
  description: string;
  details: string[];
  title: string;
}
const scheduleCallPresetContent = {
  demo: {
    badge: "Live Demo",
    description: "A guided walkthrough of the ixlayer platform — from brand brief to compliant asset delivery, with your team's questions answered in real time.",
    details: ["Watch a real brand move through creative production and medical review.", "See how approval cycles and revision workflows actually run.", "Leave with a clear picture of integration requirements and onboarding steps."],
    title: "See it in action"
  },
  project: {
    badge: "30 Minutes",
    description: "A focused conversation about your brand's patient journey and where ixlayer fits — no deck, no pitch, just a direct look at your situation.",
    details: ["Map your specific patient pathway gaps to ixlayer capabilities.", "See how brands in your therapeutic area have structured their deployment.", "Agree on a starting scope you can take back to your team."],
    title: "Talk to the team"
  }
} satisfies Record<ScheduleCallPreset, ScheduleCallPresetContent>;
function ScheduleCallDialogBody(t0) {
  const $ = _c(44);
  const {
    url,
    open,
    preset
  } = t0;
  const [loadState, setLoadState] = useState("idle");
  const presetContent = scheduleCallPresetContent[preset];
  const embedSrc = url ? `${url}${url.includes("?") ? "&" : "?"}embed=true` : "";
  let t1;
  let t2;
  if ($[0] !== open || $[1] !== url) {
    t1 = () => {
      if (open && url) {
        setLoadState("loading");
      } else {
        setLoadState("idle");
      }
    };
    t2 = [open, url];
    $[0] = open;
    $[1] = url;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogClose className="absolute right-4 top-4 z-10 rounded-sm bg-white/15 p-1.5 text-white transition-colors hover:bg-white/25"><X className="h-4 w-4" /><span className="sr-only">Close</span></DialogClose>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== presetContent.badge) {
    t4 = <Badge className="w-fit rounded-full border-white/15 bg-white/10 px-3 py-1 text-white">{presetContent.badge}</Badge>;
    $[5] = presetContent.badge;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== presetContent.title) {
    t5 = <DialogTitle className="text-3xl leading-tight">{presetContent.title}</DialogTitle>;
    $[7] = presetContent.title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== presetContent.description) {
    t6 = <DialogDescription className="max-w-md text-base leading-relaxed text-white/70">{presetContent.description}</DialogDescription>;
    $[9] = presetContent.description;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <DialogHeader className="space-y-4 text-left">{t4}{t5}{t6}</DialogHeader>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="rounded-3xl border border-white/10 bg-white/5 p-5"><div className="flex items-start gap-3"><div className="rounded-2xl bg-[oklch(0.58_0.19_245/0.18)] p-2.5"><CalendarClock className="h-5 w-5 text-[oklch(0.82_0.11_195)]" /></div><div><p className="text-sm font-semibold text-white">Book your slot</p><p className="mt-1 text-sm leading-relaxed text-white/60">Pick a time that works — no back-and-forth. A direct link opens in a new tab if the embed doesn't load.</p></div></div></div>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== presetContent.details) {
    t9 = presetContent.details.map(_temp);
    $[16] = presetContent.details;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <div className="grid gap-3">{t9}</div>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"><Clock3 className="h-4 w-4" /><span>30-minute video call</span></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex flex-wrap gap-3 pt-2 text-sm text-white/55">{t11}<div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"><MessagesSquare className="h-4 w-4" /><span>Real availability, no assistant</span></div></div>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10) {
    t13 = <div className="mt-8 grid gap-4">{t8}{t10}{t12}</div>;
    $[22] = t10;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t7) {
    t14 = <div className="bg-[oklch(0.15_0.04_250)] p-8 text-white">{t7}{t13}</div>;
    $[24] = t13;
    $[25] = t7;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== embedSrc || $[28] !== loadState || $[29] !== open || $[30] !== presetContent.title) {
    t15 = <div className="rounded-[28px] border border-[oklch(0.9_0.02_250)] bg-white p-3 shadow-[0_24px_80px_oklch(0.15_0.04_250/0.08)] sm:p-4">{embedSrc ? <div className="relative min-h-[720px] overflow-hidden rounded-[22px] bg-[oklch(0.98_0.004_250)]">{loadState !== "ready" ? <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[oklch(0.98_0.004_250/0.92)] text-center text-[oklch(0.35_0.03_250)]"><Spinner className="h-6 w-6" /><div><p className="font-semibold">Loading scheduler</p><p className="mt-1 text-sm text-[oklch(0.45_0.03_250)]">Fetching live availability from Zoom.</p></div></div> : null}{loadState === "error" ? <div className="absolute inset-0 z-20 flex items-center justify-center p-6"><div className="max-w-sm rounded-3xl border border-[oklch(0.88_0.03_250)] bg-white p-6 text-center shadow-lg"><p className="text-lg font-semibold text-[oklch(0.18_0.02_250)]">The embedded scheduler didn't load.</p><p className="mt-2 text-sm leading-relaxed text-[oklch(0.45_0.03_250)]">Use the direct booking link below to continue in a new tab.</p></div></div> : null}{open ? <iframe key={embedSrc} src={embedSrc} className="absolute inset-0 h-full w-full border-0" title={presetContent.title} onLoad={() => setLoadState("ready")} onError={() => setLoadState("error")} /> : null}</div> : <div className="flex min-h-[720px] items-center justify-center"><div className="max-w-md rounded-[28px] border border-[oklch(0.88_0.03_250)] bg-[oklch(0.99_0.003_250)] p-6 text-center"><p className="text-xl font-semibold text-[oklch(0.18_0.02_250)]">Zoom Scheduler is not configured for this CTA yet.</p><p className="mt-3 text-sm leading-relaxed text-[oklch(0.45_0.03_250)]">Add `NEXT_PUBLIC_ZOOM_SCHEDULER_DEMO_URL` or `NEXT_PUBLIC_ZOOM_SCHEDULER_TEAM_URL` to your `.env` file to enable live scheduling.</p></div></div>}</div>;
    $[27] = embedSrc;
    $[28] = loadState;
    $[29] = open;
    $[30] = presetContent.title;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <p className="text-sm text-[oklch(0.45_0.03_250)]">Powered by Zoom Scheduler</p>;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== embedSrc || $[34] !== url) {
    t17 = embedSrc ? <Button asChild={true} variant="outline" className="rounded-full border-[oklch(0.84_0.03_250)] bg-white px-4"><Link href={url}>Open in a new tab</Link></Button> : null;
    $[33] = embedSrc;
    $[34] = url;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-1">{t16}{t17}</div>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t15 || $[39] !== t18) {
    t19 = <div className="bg-[oklch(0.98_0.004_250)] p-4 sm:p-6">{t15}{t18}</div>;
    $[38] = t15;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t14 || $[42] !== t19) {
    t20 = <div className="relative grid max-h-[85vh] overflow-y-auto lg:grid-cols-[0.9fr_1.1fr]">{t3}{t14}{t19}</div>;
    $[41] = t14;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}
function _temp(detail, index) {
  return <div key={detail} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-4"><div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white">{index + 1}</div><p className="text-sm leading-relaxed text-white/70">{detail}</p></div>;
}
export function ScheduleCallButton(t0) {
  const $ = _c(22);
  const {
    url,
    children,
    className,
    preset,
    showArrow: t1,
    size: t2,
    theme: t3,
    variant: t4
  } = t0;
  const showArrow = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "default" : t2;
  const theme = t3 === undefined ? "light" : t3;
  const variant = t4 === undefined ? "default" : t4;
  const [open, setOpen] = useState(false);
  let t5;
  if ($[0] !== preset) {
    t5 = next => {
      if (next) {
        const inboundAttribution = getInboundAttribution();
        posthog.capture("schedule_call_opened", {
          ...(inboundAttribution !== null && buildInboundPostHogProperties(inboundAttribution)),
          preset
        });
      }
      setOpen(next);
    };
    $[0] = preset;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== className) {
    t6 = cn(className);
    $[2] = className;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== showArrow) {
    t7 = showArrow ? <ArrowRight className="h-5 w-5" /> : null;
    $[4] = showArrow;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== children || $[7] !== size || $[8] !== t6 || $[9] !== t7 || $[10] !== variant) {
    t8 = <DialogTrigger asChild={true}><Button type="button" size={size} variant={variant} className={t6}>{children}{t7}</Button></DialogTrigger>;
    $[6] = children;
    $[7] = size;
    $[8] = t6;
    $[9] = t7;
    $[10] = variant;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== open || $[13] !== preset || $[14] !== theme || $[15] !== url) {
    t9 = <DialogContent showCloseButton={false} className="overflow-hidden border-white/10 bg-transparent p-0 shadow-[0_32px_120px_oklch(0.08_0.02_250/0.75)] sm:max-w-6xl"><ScheduleCallDialogBody url={url} open={open} preset={preset} theme={theme} /></DialogContent>;
    $[12] = open;
    $[13] = preset;
    $[14] = theme;
    $[15] = url;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== open || $[18] !== t5 || $[19] !== t8 || $[20] !== t9) {
    t10 = <Dialog open={open} onOpenChange={t5}>{t8}{t9}</Dialog>;
    $[17] = open;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}